import * as React from 'react';
import * as ReactQuery from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import {
  listAllAnimalBreedRequest,
  listAllCompaniesRequest,
  listAllScalesRequest,
  updateScaleRequest,
} from '../apis/calyxSaaSDashboardAPI';
import { calendarValueToUTC, nowTruncatedToMinute, utcToCalendarValue } from '../utils/dateUtils';

export const BatchAdjustCameraSettingsButton = (props) => {
  const toast = React.useRef(
    /** @type {Toast} */ (undefined)
  );
  const [showAdjustCameraSettingsDialog, setShowAdjustCameraSettingsDialog] = React.useState(
    /** @type {boolean} */ (false)
  );

  const [selectedSerialNumbers, setSelectedSerialNumbers] = React.useState(
    /** @type {string[]} */ ([])
  );
  const [selectedEnableAutoShadowMode, setSelectedEnableAutoShadowMode] = React.useState(
    /** @type {boolean | null} */ (null)
  );
  const [selectedDataProcessingMode, setSelectedDataProcessingMode] = React.useState(
    /** @type {string | null} */ (null)
  );
  const [selectedCameraMode, setSelectedCameraMode] = React.useState(
    /** @type {string | null} */ (null)
  );
  const [selectedEnableDataCollection, setSelectedEnableDataCollection] = React.useState(
    /** @type {boolean | null} */ (null)
  );
  const [selectedEnableEstimateWeightByCV, setSelectedEnableEstimateWeightByCV] = React.useState(
    /** @type {boolean | null} */ (null)
  );
  const [selectedBreedModelName, setSelectedBreedModelName] = React.useState(
    /** @type {string | null} */ (null)
  );
  const [selectedMixedModeVisionBasedIntervalInSeconds, setSelectedMixedModeVisionBasedIntervalInSeconds] = React.useState(
    /** @type {number | null} */ (null)
  );
  const [selectedHistoryTime, setSelectedHistoryTime] = React.useState(
    /** @type {Date | null} */ (null)
  );

  const isCameraAdjustmentValid = React.useMemo(
    () => {
      if (selectedSerialNumbers.length === 0) return false;
      return true;
    },
    [selectedSerialNumbers.length],
  );

  function resetForm() {
    setSelectedSerialNumbers([]);
    setSelectedEnableAutoShadowMode(null);
    setSelectedDataProcessingMode(null);
    setSelectedCameraMode(null);
    setSelectedEnableDataCollection(null);
    setSelectedEnableEstimateWeightByCV(null);
    setSelectedBreedModelName(null);
    setSelectedMixedModeVisionBasedIntervalInSeconds(null);
    setSelectedHistoryTime(null);
  }

  const allScalesQuery = ReactQuery.useQuery(['scales'], () => listAllScalesRequest());
  const allCompaniesQuery = ReactQuery.useQuery(['companies'], () => listAllCompaniesRequest());
  const allBreedsQuery = ReactQuery.useQuery(['breeds'], () => listAllAnimalBreedRequest());
  const companyMap = React.useMemo(() => new Map((allCompaniesQuery.data ?? []).map((company) => [company.id, company])), [allCompaniesQuery.data]);
  const availableSerialNumbers = React.useMemo(
    () => /** @type {string[]} */ ((allScalesQuery.data ?? []).map((scale) => scale.serialNumber)),
    [allScalesQuery.data],
  );
  const availableSerialNumbersSet = React.useMemo(
    () => new Set(availableSerialNumbers),
    [availableSerialNumbers]
  );
  const groupedAvailableBreedOptions = [...Map.groupBy(
    (allBreedsQuery.data ?? []).filter((breed) => breed.modelName != null),
    (breed) => breed.managedByCompanyId
  ).entries()]
    .map(([managedByCompanyId, breeds]) => ({
      label: managedByCompanyId != null ? companyMap.get(managedByCompanyId)?.name : '[Global]',
      children: breeds.map((breed) => ({
        header: breed.breedName,
        label: breed.modelName,
        value: breed.modelName,
      })),
    }));

  async function updateScale(selectedSerialNumber) {
    const selectedScale = allScalesQuery.data?.find((scale) => scale.serialNumber === selectedSerialNumber);

    if (selectedScale == null) return;

    try {
      await updateScaleRequest({
        deviceId: selectedScale.id,
        requestBody: {
          enableAutoShadowMode: selectedEnableAutoShadowMode ?? undefined,
          dataProcessingMode: selectedDataProcessingMode ?? undefined,
          cameraMode: selectedCameraMode ?? undefined,
          enableDataCollection: selectedEnableDataCollection ?? undefined,
          enableEstimateWeightByCV: selectedEnableEstimateWeightByCV ?? undefined,
          breed: selectedBreedModelName ?? undefined,
          mixedModeVisionBasedIntervalInSeconds: selectedMixedModeVisionBasedIntervalInSeconds ?? undefined,
        },
        historyTime: selectedHistoryTime?.toISOString() ?? undefined,
      });

      toast.current.show({
        severity: 'success',
        summary: `[${selectedScale.serialNumber}] Camera Settings Adjusted`,
        detail: `Successfully adjusted camera settings of scale ${selectedScale.serialNumber}.`,
      });
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary: `[${selectedScale.serialNumber}] Unexpected Error`,
        detail: err.toString(),
        sticky: true,
      });
    }
  }

  const adjustCameraSettingsMutation = ReactQuery.useMutation(
    async () => {
      await Promise.allSettled(
        selectedSerialNumbers.map((selectedSerialNumber) => updateScale(selectedSerialNumber))
      );
    },
  );

  function editScaleListInCSV() {
    const csv = window.prompt('CSV:', selectedSerialNumbers.join(','));

    if (csv == null) return;

    const serialNumbers = csv.split(',')
      .map((e) => e.trim())
      .filter((e) => e !== '');

    setSelectedSerialNumbers(serialNumbers);
  }

  function handleEnableAutoShadowModeChange(nextEnableAutoShadowMode) {
    setSelectedEnableAutoShadowMode(nextEnableAutoShadowMode);

    if (nextEnableAutoShadowMode === true) {
      setSelectedDataProcessingMode('DataCollectionAndBirdOnScaleVisionBased');
      setSelectedMixedModeVisionBasedIntervalInSeconds(20);
    }
  }

  function handleOpenAdjustCameraSettingsDialog() {
    resetForm();
    setShowAdjustCameraSettingsDialog(true);
  }
  function handleCloseAdjustCameraSettingsDialog() {
    setShowAdjustCameraSettingsDialog(false);
  }
  async function handleAdjustCameraSettings() {
    adjustCameraSettingsMutation.mutate();
  }

  const selectedHistoryTimeCalValue = selectedHistoryTime != null ? utcToCalendarValue(selectedHistoryTime) : null;
  const nowMinuteCalValue = utcToCalendarValue(nowTruncatedToMinute());

  return (
    <>
      <Button
        label='Batch Adjust Camera Settings'
        icon='pi pi-wrench'
        className='p-button-primary'
        onClick={handleOpenAdjustCameraSettingsDialog}
        disabled
      />

      <Toast ref={toast} />

      <Dialog
        blockScroll
        visible={showAdjustCameraSettingsDialog}
        header="Batch Adjust Camera Settings"
        footer={
          <div>
            <Button
              className="p-button-primary"
              label="Batch Adjust"
              icon="pi pi-wrench"
              loading={adjustCameraSettingsMutation.isLoading}
              disabled={!isCameraAdjustmentValid}
              onClick={handleAdjustCameraSettings}
            />
          </div>
        }
        style={{ width: '450px' }}
        onHide={handleCloseAdjustCameraSettingsDialog}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-2">Target Scale(s)</label>
            <div className="flex gap-3">
              <MultiSelect
                value={selectedSerialNumbers}
                options={availableSerialNumbers}
                display="chip"
                filter
                showSelectAll={false}
                placeholder="Select Target Scale(s)"
                invalid={selectedSerialNumbers.length === 0}
                className="w-full"
                pt={{
                  label: {
                    style: {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                    },
                  },
                  token: ({ context }) => ({
                    style: {
                      margin: '0',
                      fontFamily: 'monospace',
                      color: !availableSerialNumbersSet.has(context.value) ? 'red' : undefined,
                    },
                  }),
                  item: {
                    style: {
                      fontFamily: 'monospace',
                    },
                  },
                }}
                onChange={(event) => setSelectedSerialNumbers(event.value)}
              />
              <Button
                className="flex-none"
                icon="pi pi-align-left"
                outlined
                tooltip="Edit in CSV"
                onClick={editScaleListInCSV}
              />
            </div>
          </div>
          <div>
            <label className="block mb-2">
              Enable Auto Shadow Mode
              <i
                className="pi pi-info-circle shadow-mode-tooltip-target ml-1"
                data-pr-tooltip="When enabled, Data Processing Mode is forced to Data Collection + Bird On Scale (Vision Based)."
                data-pr-position="top"
                style={{ cursor: 'pointer' }}
              />
            </label>
            <Tooltip target=".shadow-mode-tooltip-target" my="center bottom-8" at="center top" />
            <div className="flex align-items-center gap-2">
              <InputSwitch
                checked={selectedEnableAutoShadowMode === true}
                onChange={(event) => handleEnableAutoShadowModeChange(event.value)}
              />
              <span className="text-sm text-gray-500">
                {selectedEnableAutoShadowMode == null
                  ? '(Not modified)'
                  : (selectedEnableAutoShadowMode ? 'On' : 'Off')}
              </span>
              <Button
                className="flex-none ml-auto"
                icon="pi pi-times"
                outlined
                tooltip="Clear"
                disabled={selectedEnableAutoShadowMode == null}
                onClick={() => setSelectedEnableAutoShadowMode(null)}
              />
            </div>
          </div>
          <div>
            <label className="block mb-2">Data Processing Mode</label>
            <Dropdown
              value={selectedDataProcessingMode}
              disabled={selectedEnableAutoShadowMode === true}
              options={[
                { label: 'Data Collection', value: 'DataCollection' },
                { label: 'Central Area Coverage (Visual Method)', value: 'CentralAreaCoverage' },
                { label: 'Vertical Coverage (Visual Method)', value: 'VerticalCoverage', disabled: true },
                { label: 'Data Collection + Bird On Scale (Vision Based)', value: 'DataCollectionAndBirdOnScaleVisionBased' },
                { label: 'Data Collection + Bird Off Scale (Vision Based)', value: 'DataCollectionAndBirdOffScaleVisionBased' },
              ]}
              showClear
              placeholder="(Not modified)"
              className="w-full"
              onChange={(event) => setSelectedDataProcessingMode(event.value)}
            />
          </div>
          <div>
            <label className="block mb-2">Camera Mode</label>
            <Dropdown
              value={selectedCameraMode}
              options={[
                { label: 'Monocular', value: 'MonocularMode' },
                { label: 'Stereoscopic', value: 'StereoscopicMode', disabled: true },
                { label: 'ToF Depth', value: 'ToFDepthMode', disabled: true },
              ]}
              showClear
              placeholder="(Not modified)"
              className="w-full"
              onChange={(event) => setSelectedCameraMode(event.value)}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block mb-2">Enable Data Collection</label>
              <Dropdown
                value={selectedEnableDataCollection}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                showClear
                placeholder="(Not modified)"
                className="w-full"
                onChange={(event) => setSelectedEnableDataCollection(event.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block mb-2">Enable Visual Method</label>
              <Dropdown
                value={selectedEnableEstimateWeightByCV}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                showClear
                placeholder="(Not modified)"
                className="w-full"
                onChange={(event) => setSelectedEnableEstimateWeightByCV(event.value)}
              />
            </div>
          </div>
          <div>
            <label className="block mb-2">Breed Model</label>
            <Dropdown
              value={selectedBreedModelName}
              options={groupedAvailableBreedOptions}
              optionGroupLabel="label"
              optionGroupChildren="children"
              itemTemplate={(option) => (
                <div>
                  <div className="text-sm text-gray-500 mb-1">{option.header}</div>
                  <div>{option.label}</div>
                </div>
              )}
              filter
              showClear
              placeholder="(Not modified)"
              className="w-full"
              onChange={(event) => setSelectedBreedModelName(event.value)}
            />
          </div>
          {(selectedDataProcessingMode === 'DataCollectionAndBirdOnScaleVisionBased' || selectedDataProcessingMode === 'DataCollectionAndBirdOffScaleVisionBased') && (
            <div>
              <label className="block mb-2">
                Vision Based Interval (seconds)
                <i
                  className="pi pi-info-circle vision-interval-tooltip-target ml-1"
                  data-pr-tooltip="In mixed mode, the interval (in seconds) for sending data to PCV"
                  data-pr-position="top"
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <Tooltip target=".vision-interval-tooltip-target" my="center bottom-8" at="center top" />
              <InputNumber
                value={selectedMixedModeVisionBasedIntervalInSeconds}
                min={0}
                showButtons
                placeholder="(Not modified)"
                className="w-full"
                onChange={(event) => setSelectedMixedModeVisionBasedIntervalInSeconds(event.value)}
              />
            </div>
          )}
          <div>
            <label className="block mb-2">Effective Time (UTC)</label>
            <Calendar
              value={selectedHistoryTimeCalValue}
              viewDate={selectedHistoryTimeCalValue ?? nowMinuteCalValue}
              dateFormat="yy-mm-dd"
              showTime
              hourFormat="24"
              showIcon
              showButtonBar
              placeholder="Now"
              className="w-full"
              onChange={(event) => setSelectedHistoryTime(event.value != null ? calendarValueToUTC(event.value) : null)}
              onTodayButtonClick={() => setSelectedHistoryTime(nowTruncatedToMinute())}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
};
